import type {
  Student,
  Lesson,
  ScheduledNotification,
  NotificationTemplate,
  ScheduledMessage,
  StandaloneJob,
  PendingCapture,
  RecurringPending,
} from "../types";

const BASE = import.meta.env.VITE_API_URL ?? "";

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (res.status === 401) {
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text();
    let msg: string;
    try {
      const body = JSON.parse(text) as Record<string, unknown>;
      msg = (body.error ?? body.message ?? res.statusText) as string;
    } catch {
      msg = text || res.statusText;
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

// ─── Students ─────────────────────────────────────────────────────────────────

export const getStudents = () => apiFetch<Student[]>("/api/students");
export const getStudent = (id: string | number) =>
  apiFetch<Student>(`/api/students/${id}`);
export const createStudent = (body: Record<string, unknown>) =>
  apiFetch<Student>("/api/students", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const updateStudent = (
  id: string | number,
  body: Record<string, unknown>,
) =>
  apiFetch<Student>(`/api/students/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
export const logPayment = (
  id: string | number,
  body: Record<string, unknown>,
) =>
  apiFetch<Student>(`/api/students/${id}/payment`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
export const deleteStudent = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/students/${id}`, { method: "DELETE" });

// ─── Lessons ──────────────────────────────────────────────────────────────────

export const getLessons = () => apiFetch<Lesson[]>("/api/lessons");
export const getLesson = (id: string | number) =>
  apiFetch<Lesson>(`/api/lessons/${id}`);
export const createLesson = (body: Record<string, unknown>) =>
  apiFetch<Lesson>("/api/lessons", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const updateLesson = (
  id: string | number,
  body: Record<string, unknown>,
) =>
  apiFetch<Lesson>(`/api/lessons/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
export const deleteLesson = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/lessons/${id}`, { method: "DELETE" });

// ─── Notifications ────────────────────────────────────────────────────────────

export const getNotifications = () =>
  apiFetch<ScheduledNotification[]>("/api/notifications");
export const retryNotification = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/notifications/${id}/retry`, {
    method: "POST",
  });

// ─── Templates ────────────────────────────────────────────────────────────────

export const getTemplates = () =>
  apiFetch<NotificationTemplate[]>("/api/notification-templates");
export const createTemplate = (body: Record<string, unknown>) =>
  apiFetch<NotificationTemplate>("/api/notification-templates", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const updateTemplate = (
  id: string | number,
  body: Record<string, unknown>,
) =>
  apiFetch<NotificationTemplate>(`/api/notification-templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
export const deleteTemplate = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/notification-templates/${id}`, {
    method: "DELETE",
  });

// ─── Messages ─────────────────────────────────────────────────────────────────

export const getMessages = () => apiFetch<ScheduledMessage[]>("/api/messages");
export const createMessage = (body: Record<string, unknown>) =>
  apiFetch<ScheduledMessage>("/api/messages", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const cancelMessage = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/messages/${id}`, { method: "DELETE" });

export interface WaGroup { id: string; name: string }
export const getWaGroups = () => apiFetch<WaGroup[]>("/api/messages/groups");

// ─── Jobs ─────────────────────────────────────────────────────────────────────

export const getJobs = () => apiFetch<StandaloneJob[]>("/api/standalone-jobs");
export const createJob = (body: Record<string, unknown>) =>
  apiFetch<StandaloneJob>("/api/standalone-jobs", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const updateJob = (id: string | number, body: Record<string, unknown>) =>
  apiFetch<StandaloneJob>(`/api/standalone-jobs/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

// ─── Pending captures (unmatched calendar events) ─────────────────────────────

export const getCaptures = () => apiFetch<PendingCapture[]>("/api/captures");
export const getCapture = (id: string | number) =>
  apiFetch<PendingCapture>(`/api/captures/${id}`);
export const createStudentFromCapture = (
  id: string | number,
  body: Record<string, unknown>,
) =>
  apiFetch<{ studentId: number; lessonId: number }>(`/api/captures/${id}/create-student`, {
    method: "POST",
    body: JSON.stringify(body),
  });
export const dismissCapture = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/captures/${id}`, { method: "DELETE" });

// ─── Recurring pending (collapsed GCal series) ────────────────────────────────

export const getRecurring = () => apiFetch<RecurringPending[]>("/api/recurring");
export const materializeRecurring = (id: string | number) =>
  apiFetch<{ lessonId: number }>(`/api/recurring/${id}/materialize`, { method: "POST" });
export const dismissRecurring = (id: string | number) =>
  apiFetch<{ ok: boolean }>(`/api/recurring/${id}`, { method: "DELETE" });

// ─── Push notifications ───────────────────────────────────────────────────────

export const getPushVapidKey        = () => apiFetch<{ publicKey: string }>('/api/push/vapid-public-key')
export const savePushSubscription   = (sub: object) =>
  apiFetch<{ ok: boolean }>('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub) })
export const deletePushSubscription = () =>
  apiFetch<{ ok: boolean }>('/api/push/subscribe', { method: 'DELETE' })

// ─── Google Contacts ──────────────────────────────────────────────────────────

export interface GoogleContact {
  resourceName?: string | null
  name:  string
  phone: string | null
  email: string | null
}

// ─── Google Contacts cache (search + refresh) ─────────────────────────────────
// Single source of truth for Google contacts in the UI: both the Students
// importer and the Messages composer search this cache.

export interface ContactsCacheStatus { count: number; syncedAt: string | null }
export const getContactsCacheStatus = () =>
  apiFetch<ContactsCacheStatus>('/api/google/contacts/cache-status')
export const searchCachedContacts = (q: string) =>
  apiFetch<GoogleContact[]>(`/api/google/contacts/cached?q=${encodeURIComponent(q)}`)
export const refreshContacts = () =>
  apiFetch<{ count: number; syncedAt: string }>('/api/google/contacts/refresh', { method: 'POST' })

// ─── Google Calendar settings ─────────────────────────────────────────────────

export interface CalendarOption  { id: string; summary: string; primary: boolean }
export interface CalendarSettings { calendarIds: string[]; titlePrefixes: string[] }

export const getCalendars        = () => apiFetch<CalendarOption[]>('/api/google/calendars')
export const getCalendarSettings = () => apiFetch<CalendarSettings>('/api/google/calendar-settings')
export const saveCalendarSettings = (body: CalendarSettings) =>
  apiFetch<CalendarSettings>('/api/google/calendar-settings', { method: 'PUT', body: JSON.stringify(body) })

// ─── Lesson confirm (public, token-based) ─────────────────────────────────────

export interface ConfirmSummary {
  lessonId:         number
  studentName:      string
  scheduledAt:      string | null
  durationMin:      number
  status:           string
  alreadyConfirmed: boolean
}
export interface ConfirmResult {
  studentName:   string
  balance:       number
  debt:          number
  credit:        number
  totalPaid:     number
  remainingOpen: number
  nextToken:     string | null
}

export const getConfirm = (token: string) =>
  apiFetch<ConfirmSummary>(`/api/confirm/${token}`)
export const submitConfirm = (token: string, body: { happened: boolean; actualDurationMin?: number }) =>
  apiFetch<ConfirmResult>(`/api/confirm/${token}`, { method: 'POST', body: JSON.stringify(body) })
